import { Helmet } from "react-helmet-async";
import { DEFAULT_SOCIAL_LINKS } from "../socialLinks";
import { serializeJsonLd } from "./jsonLd";

const SITE_URL = "https://www.zetech.ac.ke";

/**
 * Sitewide Organization (CollegeOrUniversity) structured data.
 *
 * Rendered once from <Header /> so every page ships the same
 * organization identity — name, logo, contact points, campuses and
 * verified social profiles — instead of each page inventing its own.
 * Page-specific schema (Course, Article, BreadcrumbList…) still comes
 * from <Seo />; this only carries the publisher-level graph.
 */
export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "CollegeOrUniversity",
  "@id": `${SITE_URL}/#organization`,
  name: "Zetech University",
  alternateName: ["Zetech", "Zetech University Kenya"],
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/logo512.png`,
    caption: "Zetech University logo",
  },
  image: `${SITE_URL}/logo512.png`,
  description:
    "Zetech University is a leading technological and entrepreneurial university in Kenya offering certificate, diploma, TVET, degree and postgraduate programmes.",
  foundingDate: "1999",
  email: "info@zetech.ac.ke",
  telephone: "+254719034500",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Off Thika Road, Ruiru",
    addressLocality: "Ruiru",
    addressRegion: "Kiambu",
    addressCountry: "KE",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      name: "Admissions Office",
      telephone: "+254719034500",
      email: "admissions@zetech.ac.ke",
      // Google only recognizes a fixed vocabulary here; anything else is
      // reported as an invalid value in the Rich Results test.
      contactType: "customer service",
      areaServed: "KE",
      availableLanguage: ["English", "Swahili"],
    },
    {
      "@type": "ContactPoint",
      name: "General Enquiries",
      telephone: "+254719034500",
      email: "info@zetech.ac.ke",
      contactType: "customer support",
      areaServed: "KE",
      availableLanguage: ["English", "Swahili"],
    },
  ],
  department: [
    {
      "@type": "CollegeOrUniversity",
      name: "Zetech University — Ruiru Campus",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Off Thika Road, Ruiru",
        addressLocality: "Ruiru",
        addressCountry: "KE",
      },
    },
    {
      "@type": "CollegeOrUniversity",
      name: "Zetech University — Mang'u Campus",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Thika–Mang'u Road",
        addressLocality: "Thika",
        addressCountry: "KE",
      },
    },
  ],
  // Only absolute http(s) profiles belong in sameAs — a blank or relative
  // value is reported as an invalid URL by the structured-data tests.
  sameAs: Object.values(DEFAULT_SOCIAL_LINKS).filter((url) =>
    /^https?:\/\//i.test(String(url || "").trim())
  ),
};

export default function OrganizationSchema() {
  const json = serializeJsonLd(ORGANIZATION_SCHEMA);
  if (!json) return null;

  return (
    <Helmet>
      <script type="application/ld+json">{json}</script>
    </Helmet>
  );
}

